import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Swal from 'sweetalert2'
import useTrainingsApi from '../../list/hooks/useTrainingsApi'

const mapCertificationOptions = items => (
	(Array.isArray(items) ? items : []).map(certification => ({
		value: certification._id,
		label: certification.name,
	}))
)

const mapTrainingNameOptions = items => (
	(Array.isArray(items) ? items : []).map(trainingName => ({
		value: trainingName.name,
		label: trainingName.name,
	}))
)

export default function useTrainingAddPage() {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const { createTraining, getAllCertifications, getAllTrainingCatalog, getContentOptions } = useTrainingsApi()
	const [submitting, setSubmitting] = useState(false)
	const [certificationOptions, setCertificationOptions] = useState([])
	const [trainingNameOptions, setTrainingNameOptions] = useState([])
	const [contentOptions, setContentOptions] = useState([])
	const [loading, setLoading] = useState(true)

	const handleCancel = useCallback(() => {
		navigate('/centrumSzkolen/szkolenia')
	}, [navigate])

	const loadFormOptions = useCallback(async () => {
		setLoading(true)
		const [certificationsResult, trainingCatalogResult, contentOptionsResult] = await Promise.all([
			getAllCertifications({ limit: 999, sortColumn: 'name', sortDirection: 'asc' }),
			getAllTrainingCatalog({ limit: 999, sortColumn: 'name', sortDirection: 'asc' }),
			getContentOptions(),
		])

		if (certificationsResult.error || trainingCatalogResult.error || contentOptionsResult.error) {
			await Swal.fire(
				t('trainings.add.messages.errorTitle'),
				certificationsResult.error || trainingCatalogResult.error || contentOptionsResult.error || t('trainings.add.messages.createFailed'),
				'error'
			)
			setLoading(false)
			return
		}

		setCertificationOptions(mapCertificationOptions(certificationsResult.data))
		setTrainingNameOptions(mapTrainingNameOptions(trainingCatalogResult.data))
		setContentOptions(contentOptionsResult.data)
		setLoading(false)
	}, [getAllCertifications, getAllTrainingCatalog, getContentOptions, t])

	useEffect(() => {
		loadFormOptions()
	}, [loadFormOptions])

	const handleSubmit = useCallback(async payload => {
		if (submitting) return false

		setSubmitting(true)
		const result = await createTraining(payload)

		if (!result.success) {
			await Swal.fire(
				t('trainings.add.messages.errorTitle'),
				result.error || t('trainings.add.messages.createFailed'),
				'error'
			)
			setSubmitting(false)
			return false
		}

		const trainingId = result.data?._id || result.data?.id
		setSubmitting(false)

		if (trainingId) {
			navigate(`/centrumSzkolen/szkolenia/details/${trainingId}`)
		} else {
			navigate('/centrumSzkolen/szkolenia')
		}

		return true
	}, [createTraining, navigate, submitting, t])

	return {
		certificationOptions,
		contentOptions,
		handleCancel,
		handleSubmit,
		loading,
		submitting,
		trainingNameOptions,
	}
}
